import type { Element, ElementContent, Parent, Root } from 'hast';
import mermaid from 'mermaid';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

type MermaidBlock = {
  parent: Parent;
  index: number;
  code: string;
  originalNode: Element;
};

const LANGUAGE_PREFIX = 'language-';
const MERMAID_CONTAINER_CLASS = 'mermaid-diagram';

const isString = (value: unknown): value is string => typeof value === 'string';

const getMetaString = (node: Element) => {
  const nodeData = (node as Element & { data?: Record<string, unknown> }).data;
  const dataMeta = nodeData && isString(nodeData.meta) ? nodeData.meta : undefined;
  const properties = node.properties ?? {};
  const metaCandidates = [dataMeta, properties['meta'], properties['metastring'], properties['data-meta']].filter(
    isString,
  );

  return metaCandidates[0]?.trim() ?? '';
};

const decodeHtml = (value: string) =>
  value
    .replace(/&nbsp;/gi, '\u00a0')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\\"/g, '"');

const normalizeCode = (code: string) =>
  decodeHtml(code)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\r\n?/g, '\n')
    .trim();

const diagramKeywords = [
  'graph',
  'flowchart',
  'sequence',
  'classdiagram',
  'state',
  'erdiagram',
  'journey',
  'gantt',
  'pie',
  'mindmap',
  'timeline',
  'gitgraph',
  'quadrantchart',
  'blockdiag',
  'xychart',
];

const startsWithDiagramKeyword = (line: string) => diagramKeywords.some((keyword) => line.startsWith(keyword));

const shouldRenderMermaid = (infoLine: string, code: string) => {
  const normalizedInfo = infoLine.toLowerCase();

  if (normalizedInfo === 'mermaid') {
    return true;
  }

  const trimmedCode = code.trim();

  if (!trimmedCode) {
    return false;
  }

  const firstLine = trimmedCode.split('\n')[0]?.trim().toLowerCase() ?? '';

  if (startsWithDiagramKeyword(firstLine) || (normalizedInfo && startsWithDiagramKeyword(normalizedInfo))) {
    return true;
  }

  if (/-->/.test(code) && /\[[^\]]+\]/.test(code)) {
    return true;
  }

  return false;
};

const ensureDiagramHeader = (code: string, infoLine: string) => {
  const trimmed = code.trimStart();

  if (!trimmed) {
    return code;
  }

  const firstLine = trimmed.split('\n')[0]?.trim().toLowerCase() ?? '';

  if (startsWithDiagramKeyword(firstLine)) {
    return code;
  }

  const normalizedInfo = infoLine.toLowerCase();

  if (startsWithDiagramKeyword(normalizedInfo)) {
    return `${infoLine}${trimmed ? `\n${trimmed}` : ''}`.trim();
  }

  return `graph LR\n${code}`;
};

const collectMermaidBlocks = (tree: Root) => {
  const blocks: MermaidBlock[] = [];

  visit(tree, 'element', (node, index, parent) => {
    if (!parent || typeof index !== 'number') {
      return;
    }

    if (node.tagName !== 'pre') {
      return;
    }

    const codeNode = node.children.find(
      (child): child is Element => child.type === 'element' && child.tagName === 'code',
    );

    if (!codeNode) {
      return;
    }

    const className = codeNode.properties?.className;
    const classList = Array.isArray(className)
      ? className.filter(isString)
      : isString(className)
        ? className.split(' ')
        : [];
    const metaString = getMetaString(codeNode);
    const languageTokenRaw = classList.find((cls) => cls.startsWith(LANGUAGE_PREFIX));
    const infoLine = languageTokenRaw ? normalizeCode(languageTokenRaw.slice(LANGUAGE_PREFIX.length)) : '';

    const textContent = codeNode.children
      .filter(
        (child): child is ElementContent & { value: string } => 'value' in child && typeof child.value === 'string',
      )
      .map((child) => child.value)
      .join('');

    const bodyCode = normalizeCode(textContent);
    const normalizedInfoLine = infoLine.toLowerCase();

    const headerLine =
      infoLine && normalizedInfoLine !== 'mermaid' ? [infoLine, metaString].filter(Boolean).join(' ').trim() : '';

    const candidateCode = [headerLine, bodyCode].filter(Boolean).join('\n').trim() || bodyCode;

    const hasExplicitMermaidClass = classList.includes('mermaid');

    if (!hasExplicitMermaidClass && !shouldRenderMermaid(infoLine, candidateCode)) {
      return;
    }

    const codeToRender = ensureDiagramHeader(candidateCode, infoLine);

    blocks.push({
      parent,
      index,
      code: codeToRender,
      originalNode: node,
    });
  });

  return blocks;
};

let mermaidIsInitialized = false;

const initializeMermaid = () => {
  if (mermaidIsInitialized) {
    return;
  }

  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
  });

  mermaidIsInitialized = true;
};

const renderMermaidSvg = async (code: string) => {
  initializeMermaid();

  const renderId = `mermaid-${Math.random().toString(36).slice(2)}`;
  const { svg } = await mermaid.render(renderId, code);

  return svg;
};

const createMermaidNode = (svg: string): Element => ({
  type: 'element',
  tagName: 'div',
  properties: {
    className: [MERMAID_CONTAINER_CLASS],
    'data-mermaid': 'true',
  },
  children: [
    {
      type: 'raw',
      value: svg,
    },
  ],
});

const rehypeMermaid: Plugin<[], Root> = () => async (tree) => {
  if (typeof window === 'undefined') {
    return;
  }

  const blocks = collectMermaidBlocks(tree);

  if (!blocks.length) {
    return;
  }

  for (const { parent, index, code, originalNode } of blocks) {
    try {
      const svg = await renderMermaidSvg(code);

      parent.children[index] = createMermaidNode(svg);
    } catch {
      parent.children[index] = originalNode;
    }
  }
};

export default rehypeMermaid;
